import _slicedToArray from '@babel/runtime-corejs3/helpers/slicedToArray';
import _concatInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/concat';
import { useState, useEffect, useCallback, useRef } from 'react';
import useLatest from '../useLatest/70d83229838d1f1f54143abcad3f88dcc130955a0eeb4036f7b0531864f0de79.cfe-internal.js';

var BECAME_CONTROLLED_WARNING = 'WARNING: A component is changing from being uncontrolled to controlled. Components should not switch from being uncontrolled to controlled (or vice versa). Decide between using a controlled or uncontrolled component for the lifetime of the component. More info: https://fb.me/react-controlled-components';
var BECAME_UNCONTROLLED_WARNING = 'WARNING: A component is changing from being controlled to uncontrolled. Components should not switch from being controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled component for the lifetime of the component. More info: https://fb.me/react-controlled-components';
/**
 * An abstraction on top of useState that allows for state to either be controlled internally or externally.
 * @param initialState - The state to begin with.
 * @param onChange - An observer that is called when the state changes.
 * @param externalState - State used to override the internal state.
 * @returns An array where the first item is the current state and the second item is the state updater.
 */

function useControllableState(initialState, onChange, externalState) {
  var _useState = useState(initialState),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      replaceState = _useState2[1];

  var onChangeRef = useLatest(onChange);
  var externalStateRef = useLatest(externalState);
  var internalStateRef = useLatest(state);
  useControlledTransitionWarning(externalState);
  /**
   * The following useEffect is necessary in order to make transitions
   * from using external state, to using internal state seamless and
   * follows the behavior of React's native controllable input component
   */

  useEffect(function () {
    if (externalState !== undefined) {
      replaceState(externalState);
    }
  }, [externalState]);
  /**
   * TODO: replace the effect above with this effect
   *
   * Unfortunately, this change breaks various Menu tests because
   * the submenu code in BaseMenu is dependant on this extra render.
   * Once the submenu issue is fixed, we can use the following effect.
   * There is already a test for this in useControllableState.test.js
   * that will need to be uncommented out
   */
  // const prevExternalState = usePrevious(externalState);
  // useEffect(() => {
  //     if (prevExternalState !== undefined && externalState === undefined) {
  //         replaceState(prevExternalState);
  //     }
  // }, [externalState, prevExternalState]);

  var setState = useCallback(function (input) {
    var currentState = externalStateRef.current !== undefined ? externalStateRef.current : internalStateRef.current;
    var nextState = typeof input === 'function' ? input(currentState) : input;

    if (externalStateRef.current === undefined) {
      replaceState(nextState);
    }

    if (nextState !== currentState) {
      var _onChangeRef$current, _context;

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      (_onChangeRef$current = onChangeRef.current) === null || _onChangeRef$current === void 0 ? void 0 : _onChangeRef$current.call.apply(_onChangeRef$current, _concatInstanceProperty(_context = [onChangeRef, nextState]).call(_context, args));
    }
  }, [onChangeRef, externalStateRef, internalStateRef]);
  var actualState = externalState === undefined ? state : externalState;
  return [actualState, setState];
}
/**
 * Logs a warning if the state switches from controlled to uncontrolled (or vice versa)
 * @param externalState - The state being controlled externally.
 */


function useControlledTransitionWarning(externalState) {
  var isControlled = externalState !== undefined;
  var isControlledRef = useRef(isControlled);
  useEffect(function () {
    if (isControlled !== isControlledRef.current) {
      if (isControlled) {
        // eslint-disable-next-line no-console
        console.warn(BECAME_CONTROLLED_WARNING);
      } else {
        // eslint-disable-next-line no-console
        console.warn(BECAME_UNCONTROLLED_WARNING);
      }
    }
  }, [isControlled]);
}

export { useControllableState as default };
